import Link from '@/update-monitor/Link'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ReactElement } from 'react'

import { Loading } from '@/components/Loading'
import { SignUpLayout } from '@/components/layout/web'
import { Button } from '@/components/ui/button'
import { ContractType } from '@/gql/graphql'
import { useNoSuspenseQuery } from '@/hooks/useQuery'
import { LatestContractsQuery } from '@/queries'

import IconBack from '@/assets/icons/ic_logic-g-app_arrow-left.svg'
import { useClientOnly } from '@/hooks/useClientOnly'

export default function Agreement() {
  useClientOnly()
  const router = useRouter()
  const registrationType = router.query.registration_type as string
  const code = router.query.code as string

  const [{ data: latestContractsData, fetching }] = useNoSuspenseQuery({
    query: LatestContractsQuery,
    requestPolicy: 'cache-and-network',
  })

  if (fetching) {
    return (
      <div className="grid h-full flex-grow place-items-center">
        <Loading />
      </div>
    )
  }

  const notSignedContracts = latestContractsData?.latestContracts
    ?.sort((a) => (a.contractType === ContractType.TermOfUsage ? -1 : 1))
    .filter((contract) => !contract.signed)

  return (
    <>
      <Head>
        <title>ロジックラボ | 利用規約・プライバシーポリシーの同意</title>
      </Head>

      <div className="relative">
        <Button
          variant="outline"
          className="absolute left-2.5 top-2.5 h-8 w-14 rounded-lg pl-5 pr-2.5 text-xs md:left-5 md:h-10 md:w-[92px] md:justify-start md:rounded-xl md:pl-[38px] md:text-[17px] lg:top-[22px] lg:h-11"
          size="sm"
          shadow={false}
          onClick={router.back}
        >
          <IconBack
            width={24}
            className="absolute left-0 top-1/2 -translate-y-1/2 md:left-2 md:w-[30px]"
          />
          戻る
        </Button>
      </div>

      <div className="grow px-5 pt-[54px] md:pt-[70px] lg:pt-[80px]">
        <div className="mx-auto w-full rounded-lg bg-white px-4 py-8 md:max-w-[728px] md:pb-[60px] md:pt-[50px] lg:max-w-[940px]">
          <h3 className="mb-[17px] text-center text-[18px] font-bold md:mb-[30px] md:text-2xl lg:text-4xl">
            利用規約・プライバシーポリシー
          </h3>

          <div
            tabIndex={0}
            className="mx-auto flex h-[275px] w-full flex-col gap-8 overflow-y-auto rounded-md border border-outline px-4 py-10 md:h-[306px] md:max-w-[640px]"
          >
            {notSignedContracts?.map((contract) => (
              <div key={contract.id}>
                <h2 className="mb-4 text-center text-xl font-bold text-[#666666]">
                  {contract.title}
                </h2>

                <div className="policy-section">
                  <div
                    className="text-[15px] leading-normal text-[#666666]"
                    dangerouslySetInnerHTML={{
                      __html: contract.content,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <p className="mt-5 text-center text-xs font-medium md:mt-[30px]">
            利用規約およびプライバシーポリシーを確認し、同意の上次へ進んでださい。
          </p>

          <Button
            variant="light"
            asChild={true}
            className="mx-auto mt-5 flex w-[270px] md:mt-[50px]"
            disabled={!notSignedContracts || notSignedContracts.length === 0}
          >
            <Link
              href={{
                pathname: '/web/sign_up',
                query: {
                  agreement: 'true',
                  registration_type: registrationType,
                  code,
                },
              }}
              as="/web/sign_up"
            >
              同意する
            </Link>
          </Button>
        </div>
      </div>
    </>
  )
}

Agreement.getLayout = (page: ReactElement) => (
  <SignUpLayout title="新規アカウント登録">{page}</SignUpLayout>
)
